import utils from '@bigcommerce/stencil-utils';
import ProductDetailsBase, { optionChangeDecorator } from './product-details-base';
import 'foundation-sites/js/foundation/foundation';
import 'foundation-sites/js/foundation/foundation.reveal';
import ImageGallery from '../product/image-gallery';
import modalFactory, { alertModal, defaultModal, ModalEvents, showAlertModal } from '../global/modal';
import { cloneDeep, isEmpty, isPlainObject } from 'lodash';
import nod from '../common/nod';
import { announceInputErrorMessage } from '../common/utils/form-utils';
import forms from '../common/models/forms';
import { normalizeFormData } from './utils/api';
import { isBrowserIE, convertIntoArray } from './utils/ie-helpers';
import bannerUtils from './utils/banner-utils';
import 'regenerator-runtime/runtime';

export default class ProductDetails extends ProductDetailsBase {
    constructor($scope, context, productAttributesData = {}) {
        super($scope, context);

        this.$overlay = $('[data-cart-item-add] .loadingOverlay');
        this.imageGallery = new ImageGallery($('[data-image-gallery]', this.$scope));
        this.imageGallery.init();
        this.listenQuantityChange();
        this.$swatchOptionMessage = $('.swatch-option-message');
        this.swatchInitMessageStorage = {};
        this.swatchGroupIdList = $('[id^="swatchGroup"]').map((_, group) => $(group).attr('id'));
        this.storeInitMessagesForSwatches();

        const $form = $('form[data-cart-item-add]', $scope);

        this.addToCartValidator = nod({
            submit: $form.find('input#form-action-addToCart'),
            tap: announceInputErrorMessage,
        });

        const $productOptionsElement = $('[data-product-option-change]', $form);
        const hasOptions = $productOptionsElement.html().trim().length;
        const hasDefaultOptions = $productOptionsElement.find('[data-default]').length;
        const $productSwatchGroup = $('[id*="attribute_swatch"]', $form);
        const $productSwatchLabels = $('.form-option-swatch', $form);
        const placeSwatchLabelImage = (_, label) => {
            const $optionImage = $('.form-option-expanded', $(label));
            const optionImageWidth = $optionImage.outerWidth();
            const extendedOptionImageOffsetLeft = 55;
            const { right } = label.getBoundingClientRect();
            const emptySpaceToScreenRightBorder = window.screen.width - right;
            const shiftValue = optionImageWidth - emptySpaceToScreenRightBorder;

            if (emptySpaceToScreenRightBorder < (optionImageWidth + extendedOptionImageOffsetLeft)) {
                $optionImage.css('left', `${shiftValue > 0 ? -shiftValue : shiftValue}px`);
            }
        };

        $(window).on('load', () => {
            this.registerAddToCartValidation();
            $.each($productSwatchLabels, placeSwatchLabelImage);
        });

        if (context.showSwatchNames) {
            this.$swatchOptionMessage.removeClass('u-hidden');

            $productSwatchGroup.on('change', ({ target }) => {
                const swatchGroupElement = target.parentNode.parentNode;

                this.showSwatchNameOnOption($(target), $(swatchGroupElement));
            });

            $.each($productSwatchGroup, (_, element) => {
                const swatchGroupElement = element.parentNode.parentNode;

                if ($(element).is(':checked')) this.showSwatchNameOnOption($(element), $(swatchGroupElement));
            });
        }

        $productOptionsElement.on('change', event => {
            this.productOptionsChanged(event);
            this.setProductVariant();
        });

        $form.on('submit', event => {
            this.addToCartValidator.performCheck();

            if (this.addToCartValidator.areAll('valid')) {
                this.addProductToCart(event, $form[0]);
            }
        });

        // Update product attributes. Also update the initial view in case items are oos
        // or have default variant properties that change the view
        if ((isEmpty(productAttributesData) || hasDefaultOptions) && hasOptions) {
            const $productId = $('[name="product_id"]', $form).val();
            const optionChangeCallback = optionChangeDecorator.call(this, hasDefaultOptions);

            utils.api.productAttributes.optionChange($productId, $form.serialize(), 'products/bulk-discount-rates', optionChangeCallback);
        } else {
            this.updateProductAttributes(productAttributesData);
            bannerUtils.dispatchProductBannerEvent(productAttributesData);
        }

        $productOptionsElement.show();

        $('[name="pseudo_variants"]').on('change', event => {
            this.pseudoVariantsChanged(event);
        })
        this.pseudoVariantsSetup();
        this.qtyChange();

        this.updateProductCustomFields();

        this.previewModal = modalFactory('#previewModal')[0];

        this.productWarningToggle();

        this.altProductToggle();
    }

    registerAddToCartValidation() {
        this.addToCartValidator.add([{
            selector: '[data-quantity-change] > .form-input--incrementTotal',
            validate: (cb, val) => {
                const result = forms.numbersOnly(val);
                cb(result);
            },
            errorMessage: this.context.productQuantityErrorMessage,
        }]);

        return this.addToCartValidator;
    }

    storeInitMessagesForSwatches() {
        if (this.swatchGroupIdList.length && isEmpty(this.swatchInitMessageStorage)) {
            this.swatchGroupIdList.each((_, swatchGroupId) => {
                if (!this.swatchInitMessageStorage[swatchGroupId]) {
                    this.swatchInitMessageStorage[swatchGroupId] = $(`#${swatchGroupId} ~ .swatch-option-message`).text().trim();
                }
            });
        }
    }

    setProductVariant() {
        const unsatisfiedRequiredFields = [];
        const options = [];

        $.each($('[data-product-attribute]'), (index, value) => {
            const optionLabel = value.children[0].innerText;
            const optionTitle = optionLabel.split(':')[0].trim();
            const required = optionLabel.toLowerCase().includes('required');
            const type = value.getAttribute('data-product-attribute');

            if ((type === 'input-file' || type === 'input-text' || type === 'input-number') && value.querySelector('input').value === '' && required) {
                unsatisfiedRequiredFields.push(value);
            }

            if (type === 'textarea' && value.querySelector('textarea').value === '' && required) {
                unsatisfiedRequiredFields.push(value);
            }

            if (type === 'date') {
                const isSatisfied = Array.from(value.querySelectorAll('select')).every((select) => select.selectedIndex !== 0);

                if (isSatisfied) {
                    const dateString = Array.from(value.querySelectorAll('select')).map((x) => x.value).join('-');
                    options.push(`${optionTitle}:${dateString}`);

                    return;
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }

            if (type === 'set-select') {
                const select = value.querySelector('select');
                const selectedIndex = select.selectedIndex;

                if (selectedIndex !== 0) {
                    options.push(`${optionTitle}:${select.options[selectedIndex].innerText}`);

                    return;
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }

            if (type === 'set-rectangle' || type === 'set-radio' || type === 'swatch' || type === 'input-checkbox' || type === 'product-list') {
                const checked = value.querySelector(':checked');
                if (checked) {
                    const getSelectedOptionLabel = () => {
                        const productVariantslist = convertIntoArray(value.children);
                        const matchLabelForCheckedInput = inpt => inpt.dataset.productAttributeValue === checked.value;
                        return productVariantslist.filter(matchLabelForCheckedInput)[0];
                    };
                    if (type === 'set-rectangle' || type === 'set-radio' || type === 'product-list') {
                        const label = isBrowserIE ? getSelectedOptionLabel().innerText.trim() : checked.labels[0].innerText;
                        if (label) {
                            options.push(`${optionTitle}:${label}`);
                        }
                    }

                    if (type === 'swatch') {
                        const label = isBrowserIE ? getSelectedOptionLabel().children[0] : checked.labels[0].children[0];
                        if (label) {
                            options.push(`${optionTitle}:${label.title}`);
                        }
                    }

                    if (type === 'input-checkbox') {
                        options.push(`${optionTitle}:Yes`);
                    }

                    return;
                }

                if (type === 'input-checkbox') {
                    options.push(`${optionTitle}:No`);
                }

                if (required) {
                    unsatisfiedRequiredFields.push(value);
                }
            }
        });

        let productVariant = unsatisfiedRequiredFields.length === 0 ? options.sort().join(', ') : 'unsatisfied';
        const view = $('.productView');

        if (productVariant) {
            productVariant = productVariant === 'unsatisfied' ? '' : productVariant;
            if (view.attr('data-event-type')) {
                view.attr('data-product-variant', productVariant);
            } else {
                const productName = view.find('.productView-title')[0].innerText.replace(/"/g, '\\$&');
                const card = $(`[data-name="${productName}"]`);
                card.attr('data-product-variant', productVariant);
            }
        }
    }

    /**
     * Checks if the current window is being run inside an iframe
     * @returns {boolean}
     */
    isRunningInIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    calculatePriceFromBulk(originPrice, bulk) {
        let result = originPrice;
        if (bulk.percentOff) {
            result = originPrice * (1 - bulk.percentOff / 100);
        }
        if (bulk.price) {
            result = bulk.price;
        }
        if (bulk.priceAdjustment) {
            result = originPrice - bulk.priceAdjustment;
        }
        return result;
    }

    async pseudoVariantsSetup() {
        const $overlay = $('.pseudo-variants .loadingOverlay');

        // regular pseudo options
        if (this.context.music_video_product === 0) {
            $overlay.show();
            const fetchContent = [];
            $('.pseudo-variants .product-link').each((index, element) => {
                fetchContent.push(
                    fetch('/graphql', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + this.context.settings.storefront_api.token
                        },
                        body: JSON.stringify({
                            query: `
                            query getIdBySku {
                                site {
                                product (sku: \"` + $(element).data('value') + `\") {
                                    entityId
                                    path
                                }
                              }
                            }
                            `
                        }),
                    })
                    .then(res => res.json())
                    .then(json => {
                        if (json.data.site.product) {
                            let productId = json.data.site.product.entityId;
                            let url = json.data.site.product.path;
                            element.href = url;
                        }
                    })
                )
            })

            await Promise.all(fetchContent);
            $overlay.hide();
        }
        // music video product options
        if (this.context.music_video_product === 1) {
            $overlay.show();
            let mpn = this.context.mpn;
            const numberId = mpn.split('-')[0];
            const groupId = ['V', 'L', 'VL'];
            let split = mpn.split('-');
            window.graphql_product = {};

            const fetchContent = [];
            $('.pseudo-variants [name="pseudo_variants"]').each((index, element) => {
                let sku = split[index + 1];
                $(element).val(sku);

                fetchContent.push(
                    fetch('/graphql', {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + this.context.settings.storefront_api.token
                        },
                        body: JSON.stringify({
                            query: `
                            query getIdBySku {
                                site {
                                product (sku: \"` + sku + `\") {
                                    entityId
                                    prices {
                                        price {
                                          value
                                          currencyCode
                                        }
                                        priceRange {
                                          min {
                                            ...MoneyFields
                                          }
                                          max {
                                            ...MoneyFields
                                          }
                                        }
                                        bulkPricing {
                                          minimumQuantity
                                          maximumQuantity
                                          ... on BulkPricingFixedPriceDiscount {
                                            price
                                          }
                                          ... on BulkPricingPercentageDiscount {
                                            percentOff
                                          }
                                          ... on BulkPricingRelativePriceDiscount {
                                            priceAdjustment
                                          }
                                        }
                                    }
                                }
                              }
                            }
                            fragment MoneyFields on Money {
                              value
                              currencyCode
                            }
                            `
                        }),
                    })
                    .then(res => res.json())
                    .then(json => {
                        if (json.data.site.product) {
                            const product = json.data.site.product;
                            window.graphql_product[sku] = cloneDeep(product);

                            let price = product.prices.price.value;
                            $(element).parent().find('.option-price').text(`$${price.toFixed(2)}`);
                            let minPrice = price;
                            let maxPrice = price;

                            if (!isEmpty(product.prices.bulkPricing)) {
                                let firstTier = product.prices.bulkPricing[0];
                                let lastTier = product.prices.bulkPricing[product.prices.bulkPricing.length - 1];
                                minPrice = Math.min(this.calculatePriceFromBulk(price, lastTier), minPrice);
                                maxPrice = Math.max(this.calculatePriceFromBulk(price, firstTier), maxPrice);

                                $(element).parent().find('.option-price').text(`$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`);

                                if (product.entityId == this.context.productId) {
                                    $('[data-product-price-without-tax]', this.$scope).text(`$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`);
                                }
                            }
                        }
                    })
                )
            })

            await Promise.all(fetchContent);
            $overlay.hide();
        }
    }

    pseudoVariantsChanged(event) {
        const $overlay = $('.pseudo-variants .loadingOverlay');
        const labels = $('.pseudo-variants .option-label');
        const inputs = $('.pseudo-variants .option-input');
        const index = $(event.currentTarget).data('index')
        const sku = event.currentTarget.value;
        const $form = $('form[data-cart-item-add]', this.$scope);

        $overlay.show();
        if (!isEmpty(window.graphql_product) && window.graphql_product[sku]) {
            const productId = window.graphql_product[sku].entityId;
            utils.api.productAttributes.optionChange(productId, $form.serialize(), 'products/bulk-discount-rates', (err, response) => {
                const productAttributesData = response.data || {};
                const productAttributesContent = response.content || {};
                this.updateProductAttributes(productAttributesData);
                this.updateView(productAttributesData, productAttributesContent);
                bannerUtils.dispatchProductBannerEvent(productAttributesData);

                if (!this.checkIsQuickViewChild($form)) {
                    const $context = $form.parents('.productView').find('.productView-info');
                    modalFactory('[data-reveal]', { $context });
                }

                    const product = window.graphql_product[sku];
                    const price = product.prices.price.value;
                    $('[name=qty\\[\\]]', $form).val(1);
                    let minPrice = price;
                    let maxPrice = price;
                    if (!isEmpty(product.prices.bulkPricing)) {
                        let firstTier = product.prices.bulkPricing[0];
                        let lastTier = product.prices.bulkPricing[product.prices.bulkPricing.length - 1];
                        minPrice = Math.min(this.calculatePriceFromBulk(price, lastTier), minPrice);
                        maxPrice = Math.max(this.calculatePriceFromBulk(price, firstTier), maxPrice);

                        $('[data-product-price-without-tax]', this.$scope).text(`$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`);
                    } else {
                        $('[data-product-price-without-tax]', this.$scope).text(`$${price.toFixed(2)}`);
                    }

                $overlay.hide();
            });
        }

        $(labels).removeClass('selected');
        $(labels).filter('[data-index=' + index + ']').addClass('selected');
    }

    productWarningToggle() {
        $('#product-warnings-show-all').click(function(e) {
            e.preventDefault();
            // $('.product-warnings').toggleClass('--active');
            const $wrapper = $('.product-warnings-wrapper');
            const modal = defaultModal();
            modal.open();
            const $includeWrapper = $wrapper.addClass('product-warnings-modal-content').wrap( "<div></div>" ).parent();
            modal.updateContent($includeWrapper.html());
            modal.$modal.addClass('overflow-auto');
            modal.$modal.one(ModalEvents.close, () => {
                modal.$modal.removeClass('overflow-auto');
            })
            $('.product-warnings-title', modal.$content).removeClass('no-display');
        })
    }

    altProductToggle() {
        $('#show_all_alt_item').click(function(e) {
            e.preventDefault();
            $('#alternate-items > .productList').toggleClass('--active');
        })
    }

    updateProductCustomFields() {
        let html = $('#tab-specifications .productView-info').eq(0);
        html.empty();
        const jsonFields = $('[data-custom-fields]').data('custom-fields');

        if (jsonFields) {
            const result = new Map();
            for (let field of jsonFields) {
                if (this.context.product_custom_fields.includes(field.name)) {
                    if (result.has(field.name)) {
                        let currentValue = result.get(field.name)
                        currentValue.push(field.value)
                        result.set(field.name, currentValue);
                    } else {
                        result.set(field.name, [field.value]);
                    }
                }
            }

            for (const [key, value] of result.entries()) {
                let dt = $('<dt>', {class: "productView-info-name"}).text(key + ': ');
                let dd = $('<dt>', {class: "productView-info-value"}).text(value);
                html.append(dt).append(dd);
            }
        }
    }

    /**
     *
     * Handle product options changes
     *
     */
    productOptionsChanged(event) {
        const $changedOption = $(event.target);
        const $form = $changedOption.parents('form');
        const productId = $('[name="product_id"]', $form).val();

        // Do not trigger an ajax request if it's a file or if the browser doesn't support FormData
        if ($changedOption.attr('type') === 'file' || window.FormData === undefined) {
            return;
        }

        utils.api.productAttributes.optionChange(productId, $form.serialize(), 'products/bulk-discount-rates', (err, response) => {
            const productAttributesData = response.data || {};
            const productAttributesContent = response.content || {};
            this.updateProductAttributes(productAttributesData);
            this.updateView(productAttributesData, productAttributesContent);
            bannerUtils.dispatchProductBannerEvent(productAttributesData);

            if (!this.checkIsQuickViewChild($form)) {
                const $context = $form.parents('.productView').find('.productView-info');
                modalFactory('[data-reveal]', { $context });
            }
        });
    }

    /**
     * if this setting is enabled in Page Builder
     * show name for swatch option
     */
    showSwatchNameOnOption($swatch, $swatchGroup) {
        const swatchName = $swatch.attr('aria-label');
        const activeSwatchGroupId = $swatchGroup.attr('aria-labelledby');
        const $swatchOptionMessage = $(`#${activeSwatchGroupId} ~ .swatch-option-message`);

        $('[data-option-value]', $swatchGroup).text(swatchName);
        $swatchOptionMessage.text(`${this.swatchInitMessageStorage[activeSwatchGroupId]} ${swatchName}`);
        this.setLiveRegionAttributes($swatchOptionMessage, 'status', 'assertive');
    }

    setLiveRegionAttributes($element, roleType, ariaLiveStatus) {
        $element.attr({
            role: roleType,
            'aria-live': ariaLiveStatus,
        });
    }

    checkIsQuickViewChild($element) {
        return !!$element.parents('.quickView').length;
    }

    showProductImage(image) {
        if (isPlainObject(image)) {
            const zoomImageUrl = utils.tools.imageSrcset.getSrcset(
                image.data,
                { '1x': this.context.zoomSize },
                /*
                    Should match zoom size used for data-zoom-image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
            );

            const mainImageUrl = utils.tools.imageSrcset.getSrcset(
                image.data,
                { '1x': this.context.productSize },
                /*
                    Should match fallback image size used for the main product image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
            );

            const mainImageSrcset = utils.tools.imageSrcset.getSrcset(image.data);

            this.imageGallery.setAlternateImage({
                mainImageUrl,
                zoomImageUrl,
                mainImageSrcset,
            });
        } else {
            this.imageGallery.restoreImage();
        }
    }

    updateTierPrice(qty) {
        let product;
        const $form = $('form[data-cart-item-add]', this.$scope);
        const productId = this.context.productId;
        const pseudoSelect = $('[name="pseudo_variants"]:checked', $form).val();
        if (window.graphql_product && !isEmpty(window.graphql_product)) {
            if (pseudoSelect) {
                product = window.graphql_product[pseudoSelect];
            } else {
                for (let [key, value] of Object.entries(window.graphql_product)) {
                    if (value.entityId == productId) {
                        product = value;
                    }
                }
            }

            if (product) {
                const price = product.prices.price.value;
                if (!isEmpty(product.prices.bulkPricing)) {
                    if (qty < product.prices.bulkPricing[0].minimumQuantity) {
                        $('[data-product-price-without-tax]', this.$scope).text(`$${price.toFixed(2)}`);
                    }
                    for (let bulk of product.prices.bulkPricing) {
                        if (qty >= bulk.minimumQuantity && (qty <= bulk.maximumQuantity || !bulk.maximumQuantity)) {
                            let unitPrice = this.calculatePriceFromBulk(price, bulk);
                            $('[data-product-price-without-tax]', this.$scope).text(`$${unitPrice.toFixed(2)}`);
                        }
                    }
                } else {
                    $('[data-product-price-without-tax]', this.$scope).text(`$${price.toFixed(2)}`);
                }
            }
        }
    }

    qtyChange() {
        const $form = $('form[data-cart-item-add]', this.$scope);
        $('[name=qty\\[\\]]', $form).change(event => {
            this.updateTierPrice(event.target.value);
        })
    }

    /**
     *
     * Handle action when the shopper clicks on + / - for quantity
     *
     */
    listenQuantityChange() {
        this.$scope.on('click', '[data-quantity-change] button', event => {
            event.preventDefault();
            const $target = $(event.currentTarget);
            const viewModel = this.getViewModel(this.$scope);
            const $input = viewModel.quantity.$input;
            const quantityMin = parseInt($input.data('quantityMin'), 10);
            const quantityMax = parseInt($input.data('quantityMax'), 10);

            let qty = forms.numbersOnly($input.val()) ? parseInt($input.val(), 10) : quantityMin;
            // If action is incrementing
            if ($target.data('action') === 'inc') {
                qty = forms.validateIncreaseAgainstMaxBoundary(qty, quantityMax);
            } else if (qty > 1) {
                qty = forms.validateDecreaseAgainstMinBoundary(qty, quantityMin);
            }

            this.updateTierPrice(qty);

            // update hidden input
            viewModel.quantity.$input.val(qty);
            // update text
            viewModel.quantity.$text.text(qty);
            // perform validation after updating product quantity
            this.addToCartValidator.performCheck();
        });

        // Prevent triggering quantity change when pressing enter
        this.$scope.on('keypress', '.form-input--incrementTotal', event => {
            // If the browser supports event.which, then use event.which, otherwise use event.keyCode
            const x = event.which || event.keyCode;
            if (x === 13) {
                // Prevent default
                event.preventDefault();
            }
        });
    }

    /**
     *
     * Add a product to cart
     *
     */
    async addProductToCart(event, form) {
        const $addToCartBtn = $('#form-action-addToCart', $(event.target));
        const originalBtnVal = $addToCartBtn.val();
        const waitMessage = $addToCartBtn.data('waitMessage');

        // Do not do AJAX if browser doesn't support FormData
        if (window.FormData === undefined) {
            return;
        }

        // Prevent default
        event.preventDefault();

        // pseudo variants, change add to cart product
        let data = new FormData(form);
        if (data.has('pseudo_variants')) {
            await fetch('/graphql', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + this.context.settings.storefront_api.token
                },
                body: JSON.stringify({
                    query: `
                    query getIdBySku {
                        site {
                        product (sku: \"` + data.get('pseudo_variants') + `\") {
                            entityId
                        }
                      }
                    }
                    `
                }),
            })
            .then(res => res.json())
            .then(json => {
                if (json.data.site.product) {
                    data.set('product_id', json.data.site.product.entityId);
                }
            })
        }

        // for (let [key, value] of data.entries()) {
        //     console.log(key)
        //     console.log(value)
        // }

        $addToCartBtn
            .val(waitMessage)
            .prop('disabled', true);

        this.$overlay.show();

        // Add item to cart
        utils.api.cart.itemAdd(normalizeFormData(data), (err, response) => {
            const errorMessage = err || response.data.error;

            $addToCartBtn
                .val(originalBtnVal)
                .prop('disabled', false);

            this.$overlay.hide();

            // Guard statement
            if (errorMessage) {
                // Strip the HTML from the error message
                const tmp = document.createElement('DIV');
                tmp.innerHTML = errorMessage;

                if (!this.checkIsQuickViewChild($addToCartBtn)) {
                    alertModal().$preModalFocusedEl = $addToCartBtn;
                }

                return showAlertModal(tmp.textContent || tmp.innerText);
            }

            // Open preview modal and update content
            if (this.previewModal) {
                this.previewModal.open();

                if (window.ApplePaySession) {
                    this.previewModal.$modal.addClass('apple-pay-supported');
                }

                if (!this.checkIsQuickViewChild($addToCartBtn)) {
                    this.previewModal.$preModalFocusedEl = $addToCartBtn;
                }

                this.updateCartContent(this.previewModal, response.data.cart_item.id);
            } else {
                this.$overlay.show();
                // if no modal, redirect to the cart page
                this.redirectTo(response.data.cart_item.cart_url || this.context.urls.cart);
            }
        });

        this.setLiveRegionAttributes($addToCartBtn.next(), 'status', 'polite');
    }

    /**
     * Get cart contents
     *
     * @param {String} cartItemId
     * @param {Function} onComplete
     */
    getCartContent(cartItemId, onComplete) {
        const options = {
            template: 'cart/preview',
            params: {
                suggest: cartItemId,
            },
            config: {
                cart: {
                    suggestions: {
                        limit: 4,
                    },
                },
            },
        };

        utils.api.cart.getContent(options, onComplete);
    }

    /**
     * Redirect to url
     *
     * @param {String} url
     */
    redirectTo(url) {
        if (this.isRunningInIframe() && !window.iframeSdk) {
            window.top.location = url;
        } else {
            window.location = url;
        }
    }

    /**
     * Update cart content
     *
     * @param {Modal} modal
     * @param {String} cartItemId
     * @param {Function} onComplete
     */
    updateCartContent(modal, cartItemId, onComplete) {
        this.getCartContent(cartItemId, (err, response) => {
            if (err) {
                return;
            }

            modal.updateContent(response);

            // Update cart counter
            const $body = $('body');
            const $cartQuantity = $('[data-cart-quantity]', modal.$content);
            const $cartCounter = $('.navUser-action .cart-count');
            const quantity = $cartQuantity.data('cartQuantity') || 0;
            const $promotionBanner = $('[data-promotion-banner]');
            const $backToShopppingBtn = $('.previewCartCheckout > [data-reveal-close]');
            const $modalCloseBtn = $('#previewModal > .modal-close');
            const bannerUpdateHandler = () => {
                const $productContainer = $('#main-content > .container');

                $productContainer.append('<div class="loadingOverlay pdp-update"></div>');
                $('.loadingOverlay.pdp-update', $productContainer).show();
                window.location.reload();
            };

            $cartCounter.addClass('cart-count--positive');
            $body.trigger('cart-quantity-update', quantity);

            if (onComplete) {
                onComplete(response);
            }

            if ($promotionBanner.length && $backToShopppingBtn.length) {
                $backToShopppingBtn.on('click', bannerUpdateHandler);
                $modalCloseBtn.on('click', bannerUpdateHandler);
            }
        });
    }

    /**
     * Hide or mark as unavailable out of stock attributes if enabled
     * @param  {Object} data Product attribute data
     */
    updateProductAttributes(data) {
        super.updateProductAttributes(data);
        this.showProductImage(data.image);
    }
}
